// Navigation index, search, pins and recents.
//
// The sidebar carries roughly 104 destinations across 12 sections. At that size a tree alone is not
// a navigation system: the person who uses eight screens every day scrolls past ninety-six they
// never touch, and there is no way to find anything by name. This adds the three things that make a
// large nav usable — search, pinning, and recents — without another hand-maintained list.
//
// The index is SELF-REGISTERING. Every nav leaf reports itself on mount and withdraws on unmount,
// so what search and pins can see is exactly what the sidebar actually rendered for this user. A
// separate hardcoded list would drift the first time someone added a screen or changed a feature
// gate, and would happily offer a destination the user has no module for.
import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

const NavIndexContext = createContext(null);

const PINS_KEY = 'nav.pins';
const RECENTS_KEY = 'nav.recents';
const MAX_RECENTS = 6;

const read = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || 'null');
    return Array.isArray(v) ? v : fallback;
  } catch { return fallback; }        // private browsing, or a value from an older shape
};
const write = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* nothing we can do, and nothing that should break nav */ }
};

export function NavIndexProvider({ children }) {
  // A ref, not state: registration happens during a render pass, and setState there would loop.
  const entries = useRef(new Map());
  const [version, setVersion] = useState(0);
  const bump = useRef(null);

  const register = useCallback((entry) => {
    if (!entry || !entry.path) return undefined;
    entries.current.set(entry.path, entry);
    // Coalesce the burst of registrations on first paint into one re-render.
    if (!bump.current) bump.current = setTimeout(() => { bump.current = null; setVersion((v) => v + 1); }, 0);
    return () => {
      entries.current.delete(entry.path);
      if (!bump.current) bump.current = setTimeout(() => { bump.current = null; setVersion((v) => v + 1); }, 0);
    };
  }, []);

  const [pins, setPins] = useState(() => read(PINS_KEY, []));
  const [recents, setRecents] = useState(() => read(RECENTS_KEY, []));

  const togglePin = useCallback((path) => {
    setPins((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      write(PINS_KEY, next);
      return next;
    });
  }, []);

  // Recents are most-recent-first, de-duplicated, and never include the pins — a pinned item is
  // already one click away, so repeating it in Recents just costs a row.
  const noteVisit = useCallback((path) => {
    if (!path || path === '/') return;
    setRecents((prev) => {
      const next = [path, ...prev.filter((p) => p !== path)].slice(0, MAX_RECENTS);
      write(RECENTS_KEY, next);
      return next;
    });
  }, []);

  // `version` is in the dependency list on purpose: it is the signal that the ref-held index
  // changed, and without it `all` would be recomputed from a stale snapshot.
  const value = useMemo(() => ({
    register, pins, togglePin, recents, noteVisit,
    all: Array.from(entries.current.values()),
  }), [register, pins, togglePin, recents, noteVisit, version]);

  return <NavIndexContext.Provider value={value}>{children}</NavIndexContext.Provider>;
}

export function useNavIndex() {
  return useContext(NavIndexContext);
}

// Called by every nav leaf. Safe when the provider is absent, so a leaf rendered outside the
// sidebar (a test, a storybook) does not explode.
export function useRegisterNavItem(entry) {
  const ctx = useContext(NavIndexContext);
  const reg = ctx && ctx.register;
  const { path, label, section } = entry || {};
  const icon = entry && entry.icon;
  useEffect(() => {
    if (!reg || !path || !label) return undefined;
    return reg({ path, label, section: section || '', icon });
  }, [reg, path, label, section, icon]);
}

// Match on the label first and the section second, so typing "invoice" surfaces every Invoices
// screen and typing "plan" surfaces everything under Plan Management. Ranked so a prefix beats a
// mid-word hit — "cli" should offer Client List before Reconcile.
export function searchNav(all, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const scored = [];
  for (const e of all) {
    const label = String(e.label || '').toLowerCase();
    const section = String(e.section || '').toLowerCase();
    let score = -1;
    if (label === q) score = 0;
    else if (label.startsWith(q)) score = 1;
    else if (label.includes(q)) score = 2;
    else if (section.startsWith(q)) score = 3;
    else if (section.includes(q)) score = 4;
    if (score >= 0) scored.push({ ...e, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.label.localeCompare(b.label))
    .slice(0, 12);
}
